import { selectAllBackgroundTasks } from "@/store/background-tasks/background-tasks-selector";
import { curryAppSelector } from "@/store/reselect";
import type { RootState } from "@/store/store";
import {
  isFullAiIndexingTask,
  isVlmIndexMigrationTask,
} from "@/utils/background-tasks";
import type { GUID } from "@faro-lotv/foundation";
import { isBackgroundTaskActive } from "@faro-lotv/service-wires";
import { createSelector } from "@reduxjs/toolkit";
import type { AiState, BoxMap, ElementBoxMap } from "./ai-slice";

type State = { ai: AiState };

function selectElementBoxMap({ ai }: State): ElementBoxMap {
  return ai.elementBoxMap;
}
function selectBoundingBoxes({ ai }: State): BoxMap {
  return ai.boundingBoxes;
}

export const selectElementAiBoundingBoxMap = curryAppSelector(
  createSelector(
    [selectElementBoxMap, (state, elementId: GUID) => elementId],
    (elementBoxMap, elementId) => elementBoxMap[elementId],
  ),
);

export const selectBoundingBoxesOfElement = curryAppSelector(
  createSelector(
    [
      selectElementBoxMap,
      selectBoundingBoxes,
      (state, elementId?: GUID) => elementId,
    ],
    (elementBoxMap, boundingBoxes, elementId) => {
      if (!elementId) return [];
      const boxIds = elementBoxMap[elementId];
      return boxIds?.map((boxId) => boundingBoxes[boxId]) ?? [];
    },
  ),
);

export const selectBoundingBox = curryAppSelector(
  createSelector(
    [selectBoundingBoxes, (state, boxId: GUID) => boxId],
    (boundingBoxes, boxId) => boundingBoxes[boxId],
  ),
);

/**
 * @returns true if the AI chat should be visible
 */
export function selectIsAiChatOpen({ ai }: State): boolean {
  return ai.isChatOpen;
}

/**
 * @returns true if bounding boxes should be visible
 */
export function selectShowBoundingBoxes({ ai }: State): boolean {
  return ai.showBoundingBoxes;
}

/**
 * @returns the ID of the bounding box that should be shown in isolation, if any
 */
export function selectFocusedBoundingBoxId({ ai }: State): GUID | undefined {
  return ai.focusedBoundingBoxId;
}

/**
 * @returns true if any bounding box should be rendered for the given element,
 *   either because the "show all" toggle is active or because the element owns
 *   the currently focused bounding box.
 * @param state the current app state
 * @param elementId the ID of the element to check
 */
export const selectShouldRenderBoundingBoxes = curryAppSelector(
  createSelector(
    [
      selectShowBoundingBoxes,
      selectFocusedBoundingBoxId,
      selectBoundingBoxes,
      (state: State, elementId: GUID) => elementId,
    ],
    (showBoundingBoxes, focusedBoundingBoxId, boundingBoxes, elementId) => {
      if (showBoundingBoxes) return true;
      if (!focusedBoundingBoxId) return false;
      return boundingBoxes[focusedBoundingBoxId]?.elementId === elementId;
    },
  ),
);

/**
 * @returns the active VLM index migration task, if the AI model is currently being (re)generated
 * @param state the current app state
 */
const selectActiveVlmIndexMigrationTask = createSelector(
  [selectAllBackgroundTasks],
  (tasks) =>
    tasks.find(
      (task) =>
        isVlmIndexMigrationTask(task) && isBackgroundTaskActive(task.state),
    ),
);

/**
 * @returns true while the initial AI model is being generated (full indexing). During this time
 *   there is no model available yet, so the AI chat should be disabled.
 * @param state the current app state
 */
export function selectIsAiModelInitializing(state: RootState): boolean {
  const task = selectActiveVlmIndexMigrationTask(state);
  return !!task && isFullAiIndexingTask(task);
}

/**
 * @returns true while the AI model is being updated (incremental indexing, e.g. after a project
 *   change). The chat can still be used, but responses might not reflect the latest project data.
 * @param state the current app state
 */
export function selectIsAiModelUpdating(state: RootState): boolean {
  const task = selectActiveVlmIndexMigrationTask(state);
  return !!task && !isFullAiIndexingTask(task);
}
